<template>
  <Empty
    v-if="!appointmentsList || appointmentsList.length === 0"
    title="No appointments"
    icon="calendar"
  />
  <div
    v-for="group in groupedAppointments"
    v-else
    :key="group.label"
    class="mb-5"
  >
    <div class="mb-2 text-lg font-semibold">
      {{ group.label }}
    </div>
    <ListGroup>
      <ListGroupItem
        v-for="appointment in group.appointments"
        :key="appointment.id"
        :route="`/agreement/${appointment.agreementId}/appointments/${appointment.id}`"
        class="items-center justify-center"
        :class="{
          'line-through': appointment.status === 'cancelled',
          'text-gray-400': dayjs(appointment.time).isBefore(dayjs()),
          '!p-2': compact
        }"
      >
        <div class="flex gap-4">
          <DateTile
            :date="appointment.time"
            design="minimal"
          />
          <div class="w-full">
            <div>
              <div class="flex items-center justify-start gap-4">
                <!-- <div class="font-semibold text-lg">Finance Review</div> -->
                <div class="font-semibold text-lg">
                  {{ $t(appointment.mappedMethod?.localeKey) }}
                </div>
                <Chip
                  :label="$t(appointment.mappedStatus?.localeKey)"
                  :severity="appointment.mappedStatus?.color"
                  dot
                  size="sm"
                />
              </div>
              <div class="flex flex-col text-sm">
                <div>
                  <Icon
                    type="clock"
                    class="text-quaternary"
                  />
                  {{ dayjs(appointment.time).format('h:mma') }}
                </div>
                <div>
                  <Icon
                    type="location-dot"
                    class="text-quaternary"
                  />
                  {{ appointment.dealershipName }}
                </div>
                <div>
                  <Icon
                    type="user-tie"
                    class="text-quaternary"
                  />
                  {{ appointment.assignedToName || 'Not assigned' }}
                </div>
              </div>
            </div>
          </div>
          <div>
            <!-- <Button
              icon="ellipsis-vertical"
              plain
              @click.stop
            /> -->
          </div>
        </div>
      </ListGroupItem>
    </ListGroup>
  </div>
</template>

<script setup>
// vue
import { computed } from 'vue'
// components
import Chip from '@/components/chip/Chip.vue'
import DateTile from '@/components/date-tile/DateTile.vue'
import Empty from '@/components/empty/Empty.vue'
import Icon from '@/components/icon/Icon.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
// utilities
import { getDictionaryItem } from '@/dictionary'
// libraries
import dayjs from 'dayjs'

const props = defineProps({
  appointmentsList: {
    type: Array,
    default: null
  },
  compact: {
    type: Boolean,
    default: false
  },
  futureOnly: {
    type: Boolean,
    default: false
  }
})
const groupedAppointments = computed(() => {
  if (!props.appointmentsList) {
    return []
  }

  const now = dayjs()
  const futureAppointments = []
  const pastAppointments = []

  // Process and categorize appointments
  props.appointmentsList.forEach(appointment => {
    appointment.date = dayjs(appointment.time).format('YYYY-MM-DD')
    appointment.mappedStatus = getDictionaryItem('appointmentStatus', appointment.status)
    appointment.mappedMethod = getDictionaryItem('appointmentMethod', appointment.method)
    appointment.mappedOutcome = appointment.outcome
      ? getDictionaryItem('appointmentOutcome', appointment.outcome)
      : null

    if (dayjs(appointment.time).isAfter(now)) {
      futureAppointments.push(appointment)
    } else {
      pastAppointments.push(appointment)
    }
  })

  // Sort future appointments chronologically (earliest first)
  futureAppointments.sort((a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf())

  // Sort past appointments reverse chronologically (most recent first)
  pastAppointments.sort((a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf())

  const grouped = []

  // Add upcoming appointments first
  if (futureAppointments.length > 0) {
    grouped.push({
      label: 'Upcoming',
      appointments: futureAppointments
    })
  }

  // Add past appointments second (only if futureOnly is false)
  if (!props.futureOnly && pastAppointments.length > 0) {
    grouped.push({
      label: 'Past',
      appointments: pastAppointments
    })
  }

  return grouped
})
</script>

<style></style>
